import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import SelectWithInlineAdd from "@/components/inventory/SelectWithInlineAdd";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const emptyForm = {
  name: "", generic_name: "", company_name: "", category_id: "", location_id: "", form_id: "", unit_id: "",
  reorder_level: "0", notes: "", quantity: "", quantity_reason: "",
};

/** Every field on an item is editable here after creation, including the
 * three managed dropdowns and (when there's exactly one batch) the current
 * quantity, which is routed through the audited batch-adjustment endpoint. */
export default function EditItemDialog({ itemId, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState(emptyForm);
  const [batches, setBatches] = useState([]);
  const [refData, setRefData] = useState({ categories: [], locations: [], forms: [], units: [] });
  const { toast } = useToast();

  useEffect(() => {
    if (!open || !itemId) return;
    setLoading(true);
    Promise.all([
      api.get(`/inventory/items/${itemId}`),
      api.get("/inventory/categories", { params: { include_archived: true } }),
      api.get("/inventory/locations", { params: { include_archived: true } }),
      api.get("/inventory/forms", { params: { include_archived: true } }),
      api.get("/inventory/units", { params: { include_archived: true } }),
    ]).then(([detailRes, catRes, locRes, formRes, unitRes]) => {
      const { item, batches } = detailRes.data;
      setFields({
        name: item.name, generic_name: item.generic_name || "", company_name: item.company_name || "",
        category_id: item.category_id, location_id: item.location_id, form_id: item.form_id, unit_id: item.unit_id,
        reorder_level: String(item.reorder_level ?? 0), notes: item.notes || "",
        quantity: batches.length === 1 ? String(batches[0].quantity_remaining) : "", quantity_reason: "",
      });
      setBatches(batches);
      setRefData({ categories: catRes.data, locations: locRes.data, forms: formRes.data, units: unitRes.data });
      setLoading(false);
    });
  }, [open, itemId]);

  const setField = (key, value) => setFields((f) => ({ ...f, [key]: value }));

  const createOption = (endpoint) => async (name) => {
    const { data } = await api.post(endpoint, { name });
    const listKey = { "/inventory/categories": "categories", "/inventory/forms": "forms", "/inventory/units": "units" }[endpoint];
    setRefData((r) => ({ ...r, [listKey]: [...r[listKey], data] }));
    return data;
  };

  const originalQuantity = batches.length === 1 ? batches[0].quantity_remaining : null;
  const quantityChanged = originalQuantity !== null && Number(fields.quantity) !== originalQuantity;

  const handleSave = async () => {
    if (quantityChanged && fields.quantity_reason.trim().length < 3) {
      toast({ variant: "destructive", title: "A reason (3+ characters) is required to change the quantity" });
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/inventory/items/${itemId}`, {
        name: fields.name.trim(), generic_name: fields.generic_name.trim() || null, company_name: fields.company_name.trim() || null,
        category_id: fields.category_id, location_id: fields.location_id, form_id: fields.form_id, unit_id: fields.unit_id,
        reorder_level: Number(fields.reorder_level) || 0, notes: fields.notes,
      });
      if (quantityChanged) {
        await api.post(`/inventory/batches/${batches[0].id}/adjustment`, {
          quantity_delta: Number(fields.quantity) - originalQuantity,
          reason: fields.quantity_reason.trim(),
        });
      }
      toast({ title: "Item updated" });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save item", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-item-dialog">
        <DialogHeader><DialogTitle>Edit Item</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>Product name</Label>
              <Input value={fields.name} onChange={(e) => setField("name", e.target.value)} className="mt-1.5" data-testid="edit-item-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Generic name</Label>
                <Input value={fields.generic_name} onChange={(e) => setField("generic_name", e.target.value)} className="mt-1.5" data-testid="edit-item-generic-input" />
              </div>
              <div>
                <Label>Company name</Label>
                <Input value={fields.company_name} onChange={(e) => setField("company_name", e.target.value)} className="mt-1.5" data-testid="edit-item-company-input" />
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <div className="mt-1.5">
                <SelectWithInlineAdd
                  value={fields.category_id}
                  onChange={(v) => setField("category_id", v)}
                  options={refData.categories}
                  onCreate={createOption("/inventory/categories")}
                  placeholder="Category"
                  addLabel="Add new category"
                  testId="edit-item-category-select"
                />
              </div>
            </div>
            <div>
              <Label>Drawer</Label>
              <Select value={fields.location_id} onValueChange={(v) => setField("location_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="edit-item-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>{refData.locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{!l.active ? " (inactive)" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Form</Label>
                <div className="mt-1.5">
                  <SelectWithInlineAdd
                    value={fields.form_id}
                    onChange={(v) => setField("form_id", v)}
                    options={refData.forms}
                    onCreate={createOption("/inventory/forms")}
                    placeholder="Form"
                    addLabel="Add new form"
                    testId="edit-item-form-select"
                  />
                </div>
              </div>
              <div>
                <Label>Unit</Label>
                <div className="mt-1.5">
                  <SelectWithInlineAdd
                    value={fields.unit_id}
                    onChange={(v) => setField("unit_id", v)}
                    options={refData.units}
                    onCreate={createOption("/inventory/units")}
                    placeholder="Unit"
                    addLabel="Add new unit"
                    testId="edit-item-unit-select"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>Reorder level (low-stock threshold)</Label>
              <Input type="number" step="any" value={fields.reorder_level} onChange={(e) => setField("reorder_level", e.target.value)} className="mt-1.5" data-testid="edit-item-reorder-input" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={fields.notes} onChange={(e) => setField("notes", e.target.value)} className="mt-1.5" data-testid="edit-item-notes-input" />
            </div>

            {batches.length === 1 && (
              <div className="border border-amtm-border rounded-lg p-3 bg-amtm-bg space-y-2">
                <Label>Current quantity ({batches[0].quantity_remaining} on record)</Label>
                <Input type="number" step="any" value={fields.quantity} onChange={(e) => setField("quantity", e.target.value)} data-testid="edit-item-quantity-input" />
                {quantityChanged && (
                  <div>
                    <Label>Reason for quantity change (required)</Label>
                    <Input value={fields.quantity_reason} onChange={(e) => setField("quantity_reason", e.target.value)} className="mt-1.5" data-testid="edit-item-quantity-reason-input" />
                  </div>
                )}
              </div>
            )}
            {batches.length > 1 && (
              <p className="text-xs text-amtm-textMuted" data-testid="edit-item-multi-batch-note">
                This item has {batches.length} batches — adjust quantities per-batch from the item detail page.
              </p>
            )}

            <Button
              onClick={handleSave}
              disabled={saving || !fields.name.trim() || !fields.category_id || !fields.location_id || !fields.form_id || !fields.unit_id}
              data-testid="edit-item-save-button"
              className="bg-amtm-teal text-white hover:bg-amtm-tealHover w-full"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
